import React, { createContext, useContext, useState, useEffect } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/apiConfig';

const AuthContext = createContext();
const GUEST_USER_STORAGE_KEY = 'ydim_guest_user';
const LEGACY_AUTH_USER_KEY = 'user';
const LEGACY_AUTH_FLAG_KEY = 'isLoggedIn';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

// updated version 1.0.2 - Added Advanced Analysis Modal (Non-breaking)
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearLegacyAuthStorage = () => {
    try {
      localStorage.removeItem(LEGACY_AUTH_FLAG_KEY);
      localStorage.removeItem(LEGACY_AUTH_USER_KEY);
    } catch (_) {
      /* ignore */
    }
  };

  const loadUserFromServerSession = async () => {
    try {
      const res = await fetch(buildApiUrl(API_ENDPOINTS.AUTH_ME), {
        credentials: 'include',
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data?.authenticated || !data?.user) return null;
      return { user: data.user, isAdmin: !!data.isAdmin };
    } catch (_) {
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      const session = await loadUserFromServerSession();
      if (!cancelled && session?.user) {
        setUser(session.user);
        setIsAdmin(!!session.isAdmin);
        clearLegacyAuthStorage();
        if (!session.user.isGuest) {
          localStorage.removeItem(GUEST_USER_STORAGE_KEY);
        } else {
          try {
            localStorage.setItem(GUEST_USER_STORAGE_KEY, JSON.stringify(session.user));
          } catch (_) {
            /* ignore */
          }
        }
        setIsLoading(false);
        return;
      }

      if (!cancelled) {
        setUser(null);
        setIsAdmin(false);
      }

      // Guest compatibility fallback: localStorage when cookie not sent (legacy).
      try {
        const guestRaw = localStorage.getItem(GUEST_USER_STORAGE_KEY);
        if (guestRaw) {
          const guestUser = JSON.parse(guestRaw);
          if (guestUser?.isGuest) {
            if (!cancelled) {
              setUser(guestUser);
              setIsAdmin(false);
            }
            setIsLoading(false);
            return;
          }
          localStorage.removeItem(GUEST_USER_STORAGE_KEY);
        }
      } catch {
        localStorage.removeItem(GUEST_USER_STORAGE_KEY);
      }

      // Temporary compatibility: migrate legacy localStorage guest entry if present.
      try {
        const isLoggedIn = localStorage.getItem(LEGACY_AUTH_FLAG_KEY);
        const userData = localStorage.getItem(LEGACY_AUTH_USER_KEY);
        if (isLoggedIn === 'true' && userData) {
          const parsed = JSON.parse(userData);
          if (parsed?.isGuest) {
            localStorage.setItem(GUEST_USER_STORAGE_KEY, JSON.stringify(parsed));
            if (!cancelled) setUser(parsed);
          }
        }
      } catch {
        /* ignore corrupted legacy values */
      } finally {
        clearLegacyAuthStorage();
      }
      if (!cancelled) setIsLoading(false);
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (userData) => {
    // Intentionally ignore client-provided userData for auth truth.
    // Source of truth is server session from httpOnly cookie.
    const session = await loadUserFromServerSession();
    if (session?.user) {
      setUser(session.user);
      setIsAdmin(!!session.isAdmin);
    } else {
      setUser(null);
      setIsAdmin(false);
    }
    localStorage.removeItem(GUEST_USER_STORAGE_KEY);
    clearLegacyAuthStorage();
  };

  const enterGuestMode = (guestUser) => {
    if (!guestUser || !guestUser.isGuest) return;
    try {
      localStorage.setItem(GUEST_USER_STORAGE_KEY, JSON.stringify(guestUser));
      clearLegacyAuthStorage();
    } catch (_) {
      /* ignore storage errors but still set in-memory guest */
    }
    setUser(guestUser);
    setIsAdmin(false);
    setIsLoading(false);
  };

  const logout = async () => {
    try {
      await fetch(buildApiUrl(API_ENDPOINTS.AUTH_LOGOUT), {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_) {
      /* still clear client state */
    }
    setUser(null);
    setIsAdmin(false);
    localStorage.removeItem(GUEST_USER_STORAGE_KEY);
    clearLegacyAuthStorage();
    localStorage.removeItem('chatUserId');
    localStorage.removeItem('guest_usage_count');
  };

  return (
    <AuthContext.Provider value={{ user, isAdmin, isAuthenticated: !!user, isLoading, login, logout, enterGuestMode }}>
      {children}
    </AuthContext.Provider>
  );
};
